import { nullishFilter, arrayToObj, objectMap, byIdArrayToObj } from '@caresend/utils';
import update from 'immutability-helper';
import { isProd } from '../../database/firebaseConfig/init.js';
import { setItineraryDataInStore } from './itinerary.js';
import { setStaffUsers, setUsers } from './users.js';

/**
 * Takes an `DraftWaypointData` array and sets waypoints, draftWaypoints, itineraries, orders,
 *  bookings and procedures in the
 * appropriate store modules.
 *
 * Required mutations:
 * - `'waypoint/SET_ITINERARY' - Itinerary`
 * - `'waypoint/SET_WAYPOINTS' - ByID<EncryptedWaypoint>`
 * - `'waypoint/SET_DRAFT_WAYPOINTS' - ByID<UnencryptedDraftWaypoint>`
 * - `'orders/SET_ORDERS' - ByID<Order>`
 * - `'bookings/SET_BOOKINGS' - ByID<Booking>`
 * - `'procedures/SET_PROCEDURES' - ByID<WithFriendlyStatus<Procedure>>`
 *
 * Must pass `store` argument to enable dev logging and optional mutations.
 * In actions, you may pass `this`, if you use the following syntax:
 * ```
 *  async 'orderFlow/fetchBookingForViewing'(
 *    this: CustomRootStore,
 *    { commit, getters },
 *    bookingID,
 *  ) {
 * ```
 */
const setDraftWaypointDataInStore = (commit, draftWaypointsData, 
/** Pass store to enable dev logging and optional mutations */
store) => {
    /** Itineraries */
    const itineraryDataArray = Object.values(draftWaypointsData)
        .map((waypoint) => waypoint.itineraryData)
        .filter(nullishFilter);
    setItineraryDataInStore(commit, itineraryDataArray, store);
    /** Draft Waypoints */
    const draftWaypointDataByIdArray = Object.values(draftWaypointsData)
        .filter(nullishFilter);
    const draftaypointsWithData = arrayToObj(draftWaypointDataByIdArray, 'id');
    const draftWaypoints = objectMap(draftaypointsWithData, (waypoint) => update(waypoint, { $unset: [
            'itineraryData',
            'draftProceduresData',
            'prescriberData',
            'proceduresData',
            'supplyTransfersData',
            'waypointData',
        ] }));
    /** Waypoints */
    const waypointDataByIdArray = Object.values(draftWaypointsData)
        .map((draftWaypoint) => draftWaypoint.waypointData)
        .filter(nullishFilter);
    const waypointsWithData = arrayToObj(waypointDataByIdArray, 'id');
    const waypoints = objectMap(waypointsWithData, (procedure) => update(procedure, { $unset: [
            'waypointActionsData',
            'itineraryData',
            'placeData',
            'placeGroupData',
        ] }));
    /** Procedures */
    const procedureDataByIdArray = Object.values(draftWaypointsData)
        .map((draftWaypoint) => draftWaypoint.proceduresData)
        .filter(nullishFilter);
    const proceduresWithData = byIdArrayToObj(procedureDataByIdArray);
    const procedures = objectMap(proceduresWithData, (procedure) => update(procedure, { $unset: [
            'bookingData',
            'contactData',
            'nurseData',
            'waypointActionsData',
        ] }));
    /** Draft Procedures */
    const draftProcedureDataByIdArray = Object.values(draftWaypointsData)
        .map((draftWaypoint) => draftWaypoint.draftProceduresData)
        .filter(nullishFilter);
    const draftProceduresWithData = byIdArrayToObj(draftProcedureDataByIdArray);
    const draftProcedures = objectMap(draftProceduresWithData, (procedure) => update(procedure, { $unset: [
            'bookingData',
            'contactData',
            'nurseData',
            'waypointActionsData',
        ] }));
    const allProcedures = { ...procedures, ...draftProcedures };
    const allProceduresData = { ...proceduresWithData, ...draftProceduresWithData };
    /** Bookings */
    const bookingDataArray = Object.values(allProceduresData)
        .map((procedure) => procedure.bookingData)
        .filter(nullishFilter);
    const bookingsWithData = arrayToObj(bookingDataArray, 'id');
    const bookings = objectMap(bookingsWithData, (booking) => update(booking, { $unset: [
            'orderData',
            'patientData',
            'proceduresData',
        ] }));
    /** Orders */
    const orderDataArrayFromBookings = Object.values(bookingsWithData)
        .map((booking) => booking.orderData)
        .filter(nullishFilter);
    const orderDataArrayFromProcedures = Object.values(allProceduresData)
        .map((procedure) => procedure.orderData)
        .filter(nullishFilter);
    const orderDataArray = [...orderDataArrayFromBookings, ...orderDataArrayFromProcedures];
    const ordersWithData = arrayToObj(orderDataArray, 'id');
    const orders = objectMap(ordersWithData, (order) => update(order, { $unset: [
            'bookingsData',
            'officeData',
            'prescriberData',
            'schedulingUserData',
        ] }));
    /** Place Accounts */
    let placeAccounts = {};
    Object.values(allProceduresData)
        .forEach((procedureData) => {
        var _a;
        Object.values((_a = procedureData.placeAccountsData) !== null && _a !== void 0 ? _a : {}).forEach((placeAccountData) => {
            placeAccounts = {
                ...placeAccounts,
                [placeAccountData.id]: placeAccountData,
            };
        });
    });
    /** Set Required */
    commit('waypoint/SET_DRAFT_WAYPOINTS', draftWaypoints);
    commit('waypoint/SET_WAYPOINTS', waypoints);
    commit('orders/SET_ORDERS', orders);
    commit('bookings/SET_BOOKINGS', bookings);
    commit('procedures/SET_PROCEDURES', allProcedures);
    /** StaffUsers */
    const additionalStaffUsers = {};
    draftWaypointDataByIdArray.forEach((draftWaypointData) => {
        if (draftWaypointData.prescriberData) {
            additionalStaffUsers[draftWaypointData.prescriberData.id] = draftWaypointData.prescriberData;
        }
    });
    Object.values(allProceduresData).forEach((procedureData) => {
        if (procedureData.contactData) {
            additionalStaffUsers[procedureData.contactData.id] = procedureData.contactData;
        }
    });
    Object.values(ordersWithData).forEach((order) => {
        if (order.schedulingUserData) {
            additionalStaffUsers[order.schedulingUserData.id] = order.schedulingUserData;
        }
    });
    const { staffUsersSet, staffUsers } = setStaffUsers(commit, allProceduresData, orderDataArray, store, additionalStaffUsers);
    /** Users */
    const { usersSet, users } = setUsers(commit, bookingsWithData, store);
    /** Logs (dev mode only) */
    if (!isProd && !process.env.VUE_APP_SUPPRESS_LOGS) {
        console.info('setDraftWaypointDataInStore has set:');
        console.table({
            orders: orderDataArray.length,
            bookings: bookingDataArray.length,
            procedures: Object.values(allProceduresData).length,
            staffUsers: staffUsersSet,
            users: usersSet,
            draftWaypoints: Object.keys(draftWaypoints).length,
        });
    }
    return {
        procedures,
        orders,
        bookings,
        waypoints,
        staffUsers,
        users,
        draftWaypoints,
    };
};

export { setDraftWaypointDataInStore };
